import {
  BULANDI_REGISTRATION_MISMATCH_MESSAGE,
  bulandiEventSheetHeader,
  findMatchingRegistrationRow,
  normalizeDobToIso,
  parseBrNumeric,
} from "./bulandiRegistrationSheet";
import {
  bulandiGroupEventMinTeammateSlots,
  bulandiGroupEventTeammateSlots,
  bulandiIsGroupEventForRegistration,
  bulandiTeammateBrBandErrorLabel,
  bulandiTeammateBrNumericInAllowedBand,
  collectGroupEventTeammateEntries,
} from "../data/bulandiGroupEventRegistration";

/** @param {unknown} entry */
function teammateBrFromEntry(entry) {
  if (entry && typeof entry === "object" && "br" in entry) {
    return String(entry.br ?? "").trim();
  }
  return String(entry ?? "").trim();
}

/**
 * Validates teammate B Nos. + DOBs and builds POST fields for group events.
 * @param {object} p
 * @returns {{ error: string | null, groupTeammateCredentialsByEventName?: object, groupClearTeammateBrsByEventName?: object }}
 */
export function buildGroupEventRegistrationFields(p) {
  const {
    eligible,
    selectedIds,
    fromSheetIds,
    primaryBrRaw,
    rows,
    headers,
    groupTeammateByEventId,
    groupClearBrsByEventId,
  } = p;

  const primaryNum = parseBrNumeric(primaryBrRaw);
  if (primaryNum == null) {
    return { error: "Enter a valid B No. (e.g. B1511, BR1511, or 1511)." };
  }

  const groupTeammateCredentialsByEventName = {};
  const groupClearTeammateBrsByEventName = {};

  for (const ev of eligible) {
    if (!bulandiIsGroupEventForRegistration(ev.id)) continue;

    const headerName = bulandiEventSheetHeader(ev);
    const maxSlots = bulandiGroupEventTeammateSlots(ev.id);
    const minSlots = bulandiGroupEventMinTeammateSlots(ev.id);
    if (maxSlots <= 0) continue;

    const selected = selectedIds.has(ev.id);
    const wasOnSheet = fromSheetIds.has(ev.id);
    const rawArr = Array.isArray(groupTeammateByEventId[ev.id])
      ? groupTeammateByEventId[ev.id]
      : [];
    const trimmed = [];
    for (let i = 0; i < maxSlots; i += 1) {
      trimmed.push(rawArr[i] ?? { br: "", dob: "" });
    }
    const filledBr = trimmed
      .map((t) => teammateBrFromEntry(t))
      .filter(Boolean);
    const anyFilled = filledBr.length > 0;

    if (selected) {
      if (wasOnSheet && !anyFilled) {
        continue;
      }

      if (!wasOnSheet && filledBr.length < minSlots) {
        return {
          error: `Enter at least ${minSlots} teammate B No.${minSlots > 1 ? "s" : ""} and date${minSlots > 1 ? "s" : ""} of birth for ${ev.name}${
            maxSlots > minSlots ? ` (up to ${maxSlots} teammates)` : ""
          }.`,
        };
      }

      if (wasOnSheet && anyFilled && filledBr.length < minSlots) {
        return {
          error: `Enter at least ${minSlots} teammate B Nos. and dates of birth for ${ev.name}, or clear every teammate field to keep your existing team on file.`,
        };
      }

      if (!anyFilled && !wasOnSheet && minSlots > 0) {
        return {
          error: `Enter at least ${minSlots} teammate B No.${minSlots > 1 ? "s" : ""} and date${minSlots > 1 ? "s" : ""} of birth for ${ev.name}.`,
        };
      }

      if (!anyFilled) {
        continue;
      }

      const collected = collectGroupEventTeammateEntries(trimmed, ev.id, ev.name);
      if (collected.error) {
        return { error: collected.error };
      }

      const seen = new Set([primaryNum]);
      const outMates = [];
      for (const { br: brStr, dob: dobRaw } of collected.entries || []) {
        const dobIso = normalizeDobToIso(dobRaw);
        if (!dobIso || !/^\d{4}-\d{2}-\d{2}$/.test(dobIso)) {
          return {
            error: `Enter a valid date of birth for teammate ${brStr} (${ev.name}).`,
          };
        }
        const n = parseBrNumeric(brStr);
        if (n == null) {
          return { error: `Invalid teammate B No. for ${ev.name}.` };
        }
        if (seen.has(n)) {
          return {
            error: `Each B No. in ${ev.name} must be different (including yours).`,
          };
        }
        seen.add(n);
        if (!bulandiTeammateBrNumericInAllowedBand(n, ev.id)) {
          return {
            error: `${bulandiTeammateBrBandErrorLabel(ev.id)} (${ev.name}).`,
          };
        }
        try {
          findMatchingRegistrationRow(rows, headers, brStr, dobRaw);
        } catch (e) {
          const msg = e?.message || "";
          if (msg === BULANDI_REGISTRATION_MISMATCH_MESSAGE) {
            return {
              error: `Teammate B No. and date of birth do not match our records: ${brStr} (${ev.name}).`,
            };
          }
          return { error: msg || `Could not verify teammate ${brStr} (${ev.name}).` };
        }
        outMates.push({ br: brStr, dob: dobIso });
      }

      if (outMates.length < minSlots) {
        return {
          error: `Enter at least ${minSlots} teammate B Nos. and dates of birth for ${ev.name}.`,
        };
      }

      groupTeammateCredentialsByEventName[headerName] = outMates;
      continue;
    }

    const clearArr = Array.isArray(groupClearBrsByEventId[ev.id])
      ? groupClearBrsByEventId[ev.id]
      : [];
    const clearTrimmed = clearArr.map((x) => String(x ?? "").trim()).filter(Boolean);
    if (clearTrimmed.length > 0) {
      for (const brStr of clearTrimmed) {
        const cn = parseBrNumeric(brStr);
        if (cn == null) {
          return { error: `Invalid teammate B No. to clear for ${ev.name}.` };
        }
        if (!bulandiTeammateBrNumericInAllowedBand(cn, ev.id)) {
          return {
            error: `${bulandiTeammateBrBandErrorLabel(ev.id)} (${ev.name}).`,
          };
        }
      }
      groupClearTeammateBrsByEventName[headerName] = clearTrimmed;
    }
  }

  return {
    error: null,
    groupTeammateCredentialsByEventName,
    groupClearTeammateBrsByEventName,
  };
}
